/* Common form processors */
import { handle_null } from "Util";
import * as Form from "Sauce/Form";
import { PhoneNumber } from "Sauce/Validation/PhoneNumber";
import * as Util from "Util";
import * as _ from "lodash";

export class ParseResult<T> {
    process_exception?: ExceptionInformation;
    format_exception?: ExceptionInformation;
	constructor(
		public raw: string, 
		public value: T | null, 
        public formatted: string,
        public errorMsg: string | undefined = undefined,
        public valid: boolean = true) {}
}

/* Processing User Input.

	The user makes changes to forms which almost always enter the system as a raw string.
	The string is processed into a T, which is what the rest of our app consumes.

    The string goes through several optional steps. If any of these functions throw, the exception is caught
    and the value is marked invalid.

    raw is a string that is exactly what the user typed.
        - Sauce forms display this in the box when the box is dirty. (After a change and before a blur).

    preformat
        - Transform raw into another string. You may want to do some string clean-up, such as trimming,
        before you run any other steps.
        
    prevalidate
        - If it returns a true value, the whole processing chain is terminated and the result is an invalid input.
        - errorMsg is set to the return value.
        - Default returns false.

    process
        - Transform your preformatted input string into a T. 
        - Defaults to the preformatted string
    
    postvalidate   
        - If it returns a true value, the whole processing chain is terminated and the result is an invalid input.
        - errorMsg is set to the return value.
        - Default returns false.

    format
        - This should be a string representation of how your ValueProcessor has interpreted the raw input.
        - Sauce forms display this when the user blurs the form.
     
    Handling Invalid Data	
        Invalid raw data is signaled by value.valid being false. This happens in one of three ways:
            1. prevalidate returns false.
            2. throwing from any provided callback. (The exception will be caught and placed into process_exception or format_exception).
            3. postvalidate returns false.

	Formatted Data 
        Sauce forms display the raw value while the form is dirty.
        Sauce forms display the formatted result after flush.

    Styles
        Sauce forms automatically style themselves with 'invalid' if their current ParseResult is not valid.
*/
export type ValueElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
export type ValueSource = React.FormEvent<ValueElement> | string | number | boolean;

function isEv(v: ValueSource): v is React.FormEvent<ValueElement> {
	return v && (v as React.FormEvent<ValueElement>).currentTarget !== undefined;
}
function isStr(v: ValueSource): v is string {
	return typeof v === 'string';
}

export interface ValueProcessor<T> {
	(source: ValueSource): ParseResult<T>;
}
export function extract_raw(v: ValueSource): string {
    if (isEv(v)) {
        if (v.currentTarget.type === 'checkbox' ) {
            return (v.currentTarget as any).checked ? "1" : ""; // Value processors work on strings, this is kind of hacky
        }
        else {
            return v.currentTarget.value;
        }
    }
    else if (isStr(v)) {
        return v;
    }
    else if (typeof(v) === 'number') {
        return v.toString();
    }
    else if (typeof(v) === 'boolean') {
        return v ? "1" : "";
    }
    else return "";
}
export class ValueProcessorFactory<T> {
	constructor(protected process?: (preformatted: string) => T,
				protected postvalidate?: (processed: T | null, preformatted?: string) => string,
				protected format?: (processed: T | null, preformatted?: string) => string,
				protected prevalidate?: (preformatted: string) => string,
                protected preformat?: (raw: string) => string) {}

	make(): ValueProcessor<T> {
		let prevalidate = this.prevalidate ? this.prevalidate : () => "";
		let process = this.process ? this.process : (s: any) => s;
		let postvalidate = this.postvalidate ? this.postvalidate : () => "";
        let format = this.format ? this.format : (processed: T, preformatted: string) => preformatted;
        let preformat = this.preformat ? this.preformat : (raw: string) => raw;

		return (source: ValueSource) => {
            let raw = extract_raw(source);
            let processed: T | null = null;
            let errMsg: string | undefined;
            let formatted: string = raw;
            let valid = true;
            let process_exception: ExceptionInformation | undefined;
            let format_exception: ExceptionInformation | undefined;

            try {
                formatted = preformat(raw);
                try {
                    errMsg = prevalidate(formatted);
                    valid = !errMsg;
                    if (valid) {
                        try {
                            processed = process(formatted);
                            errMsg = postvalidate(processed, formatted);
                            valid = !errMsg;
                            try {
                                formatted = format(processed, formatted);
                            }
                            catch (e) {
                                /* formatting errored */
                                valid = false;
                                formatted = raw;
                                errMsg = e.message;
                                format_exception = e;
                            }
                        }
                        catch (e) {
                            /* processing or postvalidating errored */
                            valid = false;
                            processed = null;
                            errMsg = e.message;
                            process_exception = e;
                        }
                    }
                    else {
                        processed = null;
                    }
                }
                catch (e) {
                    /* prevalidating errored */
                    processed = null;
                    valid = false;
                    process_exception = e;
                }
            }
            catch (e) {
                /* preformatting errored */
                valid = false;
                processed = null;
                errMsg = e.message;
                format_exception = e;
            }

            let rv = new ParseResult(raw, processed, formatted, errMsg, valid);
            rv.process_exception = process_exception;
            rv.format_exception = format_exception;
            return rv;
        }
    }
}



// WEBPACK FOOTER //
// ./src/code/Sauce/Validation.ts